import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, getInviteCode } from "../lib/api";
import { sanitizeArtifactContent } from "../utils/sanitizeArtifact";
import type { ChatMessage as ChatMessageType } from "../lib/types";
import Layout from "../components/Layout";
import ChatMessage, { TypingIndicator } from "../components/ChatMessage";
import ChatInput from "../components/ChatInput";

export default function Review() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project");

  const [businessCase, setBusinessCase] = useState("");
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auth guard
  useEffect(() => {
    if (!getInviteCode()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Redirect if no project param
  useEffect(() => {
    if (!projectId) navigate("/dashboard", { replace: true });
  }, [projectId, navigate]);

  // Load review state on mount
  useEffect(() => {
    if (!projectId) return;

    api.getReview(projectId)
      .then(async (res) => {
        setBusinessCase(res.business_case);
        setMessages(res.messages);
        // If no messages yet, auto-start the review conversation
        if (res.messages.length === 0 && res.phase === "business_case_review") {
          setIsLoading(true);
          try {
            const startRes = await api.startReview(projectId);
            setMessages([{ role: "assistant", content: startRes.message }]);
          } catch {
            // Silently fail — user can still chat
          } finally {
            setIsLoading(false);
          }
        }
      })
      .catch(() => {
        navigate("/dashboard", { replace: true });
      })
      .finally(() => setPageLoading(false));
  }, [projectId, navigate]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isLoading]);

  async function handleSend(text: string) {
    if (!projectId) return;
    const userMsg: ChatMessageType = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await api.sendReviewMessage(projectId, text);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.message },
      ]);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Something went wrong";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${errMsg}. Please try again.` },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleApprove() {
    if (!projectId) return;
    setActionLoading(true);
    try {
      await api.approveBusinessCase(projectId, "review");
      navigate(`/chat?project=${projectId}`);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Failed to approve";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${errMsg}` },
      ]);
      setActionLoading(false);
    }
  }

  if (pageLoading) {
    return (
      <Layout showHeader>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-500 text-sm">Loading review...</p>
        </div>
      </Layout>
    );
  }

  // Sanitize once, then derive preview
  const cleanContent = sanitizeArtifactContent(businessCase);
  const previewLines = cleanContent.split("\n").slice(0, 10).join("\n");
  const hasMore = cleanContent.split("\n").length > 10;

  return (
    <Layout showHeader>
      {/* BC Preview Panel */}
      {businessCase && (
        <div className="flex-shrink-0 border-b border-slate-700/50 bg-slate-800/40">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-300">
                Business Case Draft
              </h3>
              <div className="flex items-center gap-3">
                <button
                  disabled={downloadingPdf}
                  onClick={async () => {
                    if (!projectId || downloadingPdf) return;
                    setDownloadingPdf(true);
                    try {
                      await api.downloadPipelineStage(projectId, "business_case");
                    } catch {
                      // Fallback: download as markdown
                      const blob = new Blob([cleanContent], { type: "text/markdown" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "business-case.md";
                      a.click();
                      URL.revokeObjectURL(url);
                    } finally {
                      setDownloadingPdf(false);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-xs text-green-400 hover:text-green-300 disabled:text-slate-500 disabled:cursor-wait transition-colors"
                >
                  {downloadingPdf ? (
                    <>
                      <div className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
                {hasMore && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {expanded ? "Collapse" : "Expand"}
                  </button>
                )}
              </div>
            </div>
            <div
              className={`prose prose-invert prose-sm max-w-none prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-table:text-sm prose-td:text-slate-300 prose-th:text-slate-200 ${
                !expanded ? "max-h-48 overflow-hidden" : "max-h-[70vh] overflow-y-auto"
              }`}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {expanded ? cleanContent : previewLines}
              </ReactMarkdown>
            </div>
            {!expanded && hasMore && (
              <div className="h-8 bg-gradient-to-t from-slate-800/40 to-transparent -mt-8 relative" />
            )}
          </div>
        </div>
      )}

      {/* Chat messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {messages.map((msg, i) => (
            <ChatMessage key={i} role={msg.role} content={msg.content} />
          ))}

          {isLoading && <TypingIndicator />}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex-shrink-0 border-t border-slate-700/50 bg-slate-900/80 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
          <button
            onClick={handleApprove}
            disabled={actionLoading || isLoading}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-5 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? "Approving..." : "Approve & Continue"}
          </button>
        </div>
      </div>

      {/* Chat input */}
      <ChatInput
        onSend={handleSend}
        disabled={isLoading || actionLoading}
        placeholder="Ask about the business case..."
      />
    </Layout>
  );
}
